import {
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  Theme,
  styled,
  Hidden,
  Tooltip,
} from '@quillbot/design-system';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import Close from '@mui/icons-material/Close';
import { RootState } from 'src/_common/models/redux/store';
import { CHAR_LIMITS } from 'config/constants';
import React from 'react';
import { useSelector } from 'react-redux';
import { Check, InfoOutlined } from '@mui/icons-material';
import Carousel, { PagingDots } from 'nuka-carousel';
import { numberWithCommas } from '@Common/handlers/helper';
import CONSTANTS from 'src/paraphraser/constants';
import BadgePrimary from '@Common/components/BadgePrimary';
import ABTest from '@Common/hoc/ABTest';

const useStyles = makeStyles((theme) => ({
  root: {},
  tableWrapper: {
    display: 'flex',
  },
  mobileSlider: {
    '& .slider-slide': {
      '&:first-child': {
        zIndex: 2,
      },
    },
    '& .slider-control-bottomcenter': {
      bottom: '-48px !important',
      '& .paging-item button svg': {
        display: 'none',
      },
      '& .paging-item': {
        marginLeft: 6,
      },
      '& .paging-item button': {
        width: '10px',
        height: '10px',
        borderRadius: '100px',
        opacity: '1 !important',
        backgroundColor: `${theme.specialColors.CIRCULAR_PROGRESS}  !important`,
        padding: 0,
      },
      '& .paging-item.active button': {
        backgroundColor: `${theme.palette.primary.main} !important`,
        width: '18px',
        borderRadius: '50px',
      },
    },
  },
  freeCard: {
    // padding: '16px 22px 32px 16px',
    marginRight: '-16px',
    borderRadius: '10px',
    overflow: 'hidden',
    // backgroundColor: theme.palette.background.default,
    zIndex: 1,
    [theme.breakpoints.down('md')]: {
      marginRight: '0',
      borderRadius: '0 10px 10px 0',
      // padding: '16px 16px 32px 32px',
      marginLeft: '-10px',
    },
  },

  premiumCard: {
    // padding: '16px 20px 32px 16px',
    backgroundColor: theme.specialColors.THREE_BEAM,
    boxShadow: theme.shadows[3],
    borderRadius: 10,
    zIndex: 2,
    overflow: 'hidden',
  },
  freeCardWrapper: {},
  cardTitle: {
    padding: '8px 18px',
    backgroundColor: theme.palette.primary.main,
    fontSize: '16px',
    fontWeight: 700,
    textTransform: 'uppercase',
    color: 'white',
    // borderRadius: '10px 0 0 0',
  },
  premiumTitle: {
    backgroundColor: theme.palette.secondary.main,
  },
  cardItem: {
    // marginTop: 22,
    // paddingBottom: '18px',
    minHeight: '52px',
    padding: '0 18px',
    fontSize: '14px',
    lineHeight: '19px',
    display: 'flex',
    borderBottom: '1px solid',
    borderBottomColor: theme.palette.divider,
    alignItems: 'center',
    '&:nth-child(2)': {
      minHeight: '64px',
    },
  },
  checkIcon: {
    marginRight: 8,
  },
  premiumIcon: {
    color: theme.palette.secondary.main,
  },
  plagCheckTerms: {
    fontSize: '14px',
    lineHeight: '19px',
    textAlign: 'center',
    marginTop: '10px',
  },
  noMargin: {
    margin: '0px',
  },
  iconList: {
    padding: '2px 0px',
    marginBottom: '45px',
    '& li': {
      padding: '4px 0px',
      margin: '4px 0px 12px',
      alignItems: 'flex-start',
      [theme.breakpoints.down('sm')]: {
        margin: '0px 0px 0px',
      },
      [theme.breakpoints.down(376)]: {
        alignItems: 'flex-start',
      },
    },
  },
  listCheckIcon: {
    marginRight: '10px',
    [theme.breakpoints.down(376)]: {
      marginTop: '5px',
    },
  },

  listItemText: {
    '& span': {
      lineHeight: '21px',
      fontSize: '14px',
      [theme.breakpoints.down('md')]: {
        fontSize: '14px',
      },
    },
  },
  premiumTooltip: {
    backgroundColor: '#323232',
    padding: '8px 14px 18px',
    maxWidth: 296,
    // right: -5,
  },
  tooltipTitle: {
    color: theme.specialColors.WARNING_BAR,
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 8,
    lineHeight: '1.5',
  },
  tooltipSubtext: {
    fontSize: 13,
    lineHeight: '17.7px',
    color: theme.specialColors.WHITE_TEXT,
  },
  tooltipImg: {
    maxWidth: '100%',
    marginTop: 12,
  },
  tooltipIcon: {
    cursor: 'pointer',
    color: theme.specialColors.GRAY_BORDER_COLOR,
    '&:hover': {
      color: theme.specialColors.WARNING_BAR,
    },
  },
  tooltipArrow: {
    color: '#323232',
  },
}));

const CheckIcon = styled(Check)<{ premium?: boolean }>`
  && {
    color: ${({ theme, premium }: { theme: Theme; premium?: boolean }) =>
      premium ? theme.palette.secondary.main : theme.palette.primary.main};
  }
`;

const freeList = [
  {
    title: '125 words in the Paraphraser',
    included: true,
  },
  {
    title: 'Standard and Fluency modes',
    included: true,
  },
  {
    title: '3 synonyms options',
    included: true,
  },
  {
    title: '1 Freeze word or phrase',
    included: true,
  },
  {
    title: '1200 words in the Summarizer',
    included: true,
  },
  {
    title: 'Faster processing speed',
    included: false,
  },
  {
    title: 'Advanced grammar rewrites',
    included: false,
  },
  {
    title: 'Compare Modes (Desktop only)',
    included: false,
  },
  {
    title: 'Plagiarism Checker*',
    included: false,
  },
];

const premiumList = [
  {
    title: 'Unlimited words in the Paraphraser',
    tooltip: {
      heading: 'Write Faster',
      subText: 'You’ll complete your work up to 75% faster with QuillBot.',
    },
  },
  {
    title: 'Standard, Fluency, Expand, Shorten, Formal, Simple, and Creative modes',
    tooltip: {
      heading: 'Explore Multiple Styles',
      subText: "There's a mode for every occasion, whether formal or casual.",
      imgUrl: '/images/theme/commonImages/premiumPage/multiple-styles-tooltip.png',
    },
  },
  {
    title: '4 synonyms options',
    tooltip: {
      heading: 'Add More Synonyms',
      subText: 'Full use of the Synonym Slider can increase the uniqueness of your text.',
      imgUrl: '/images/theme/commonImages/premiumPage/synonyms-tooltip.png',
    },
  },
  {
    title: 'Unlimited Freeze words and phrases',
    tooltip: {
      heading: 'Prevent Words From Changing',
      subText:
        "Industry-specific terms (technical, scientific, medical, etc.) won't get changed during paraphrasing.",
      imgUrl: '/images/theme/commonImages/premiumPage/freeze-tooltip.png',
    },
  },
  {
    title: '6000 words in the Summarizer',
    tooltip: {
      heading: 'Find The Key Points',
      subText: 'Research time is greatly reduced when articles are trimmed to their main points.',
      imgUrl: '/images/theme/commonImages/premiumPage/key-points-tooltip.png',
    },
  },
  {
    title: 'Faster processing speed',
    tooltip: {
      heading: 'Enjoy Increased Paraphrasing Speed',
      subText:
        'Fifteen sentences are paraphrased at once instead of only two sentences with a free account.',
      imgUrl: '/images/theme/commonImages/premiumPage/paraphrasing-speed-tooltip.png',
    },
  },
  {
    title: 'Advanced grammar rewrites',
    tooltip: {
      heading: 'Get Immediate Recommendations',
      subText:
        "Rewritten sentences are suggested for text that is too complicated or doesn't flow well.",
      imgUrl: '/images/theme/commonImages/premiumPage/recommendations-tooltip.png',
    },
  },
  {
    title: 'Compare Modes (Desktop only)',
    tooltip: {
      heading: 'Choose The Best Rewrite',
      subText: "You'll be able to compare outputs from multiple modes.",
      imgUrl: '/images/theme/commonImages/premiumPage/compare-modes-tooltip.png',
    },
  },
  {
    title: 'Plagiarism Checker*',
    tooltip: {
      heading: 'Detect Plagiarism',
      subText:
        'You can avoid accidental plagiarism by ensuring all outside sources are properly cited.',
      imgUrl: '/images/theme/commonImages/premiumPage/plagiarism-tooltip.png',
    },
  },
];

interface TooltipItem {
  heading: string;
  subText: string;
  imgUrl?: string;
}
interface ListItemProps {
  title: string;
  included?: boolean;
  tooltip?: TooltipItem;
  premium?: boolean;
}

const ComparisonListItem = ({ title, included = true, tooltip, premium = true }: ListItemProps) => {
  const classes = useStyles();

  return (
    <ListItem className={classes.cardItem}>
      {included &&
        (premium ? (
          <Check className={`${classes.checkIcon} ${classes.premiumIcon}`} />
        ) : (
          <Check color="primary" className={classes.checkIcon} />
        ))}
      {!included && <Close color="error" className={classes.checkIcon} />}

      <ListItemText className={`${classes.noMargin} ${classes.listItemText}`} primary={title} />
      {tooltip && (
        <Tooltip
          classes={{
            tooltip: classes.premiumTooltip,
            arrow: classes.tooltipArrow,
          }}
          enterTouchDelay={50}
          leaveTouchDelay={2500}
          arrow
          placement="bottom-end"
          title={
            <>
              <div className={classes.tooltipTitle}>{tooltip.heading}</div>
              <div className={classes.tooltipSubtext}>{tooltip.subText}</div>
              {tooltip.imgUrl && (
                <img className={classes.tooltipImg} src={tooltip.imgUrl} alt={tooltip.heading} />
              )}
            </>
          }
        >
          <InfoOutlined className={classes.tooltipIcon} />
        </Tooltip>
      )}
    </ListItem>
  );
};

export default function UpgradeComparisonB() {
  const isMobile = useSelector((state: RootState) => state.common.platform.isMobile);
  const classes = useStyles();
  return (
    <>
      <div className={classes.root}>
        {isMobile ? (
          <Carousel
            className={classes.mobileSlider}
            // slidesToShow={0.75}
            slideWidth={0.8}
            renderCenterLeftControls={null}
            renderCenterRightControls={null}
            frameOverflow="visible"
            framePadding="0 12px 32px"
          >
            <PremiumTable />
            <FreeTable />
          </Carousel>
        ) : (
          <div className={classes.tableWrapper}>
            <div style={{ flex: 1 }}>
              <FreeTable />
            </div>
            <div style={{ flex: 1.2 }}>
              <PremiumTable />
              <div className={classes.plagCheckTerms}>*Scan up to 20 pages a month</div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function FreeTable() {
  const classes = useStyles();

  const tooltip = {
    heading: 'Explore Multiple Styles',
    subText: "There's a mode for every occasion, whether formal or casual.",
    imgUrl: '/images/theme/commonImages/premiumPage/multiple-styles-tooltip.png',
  };

  return (
    <div className={classes.freeCard}>
      <div className={classes.cardTitle}>Free</div>
      <Box border="1px solid" borderTop={0} borderRadius="0 0 10px 10px" borderColor="divider">
        {/* <Box paddingTop={7} /> */}
        {/* <ComparisonListItem title="hi" tooltip={tooltip} /> */}
        {freeList.map((freeListItem, index) => (
          <ComparisonListItem
            premium={false}
            title={freeListItem.title}
            included={freeListItem.included}
            key={`${freeListItem.title}-${index}`}
          />
        ))}
        {/* <Box paddingTop={13} /> */}
      </Box>
    </div>
  );
}

function PremiumTable() {
  const classes = useStyles();
  return (
    <div className={classes.premiumCard}>
      <div className={clsx(classes.cardTitle, classes.premiumTitle)}>Premium</div>
      <div>
        {/* <Box paddingTop={7} /> */}

        <ABTest experiment="premiumTooltips_May2022" default>
          <React.Fragment>
            {premiumList.map((premiumItem, index) => (
              <ComparisonListItem
                title={premiumItem.title}
                tooltip={premiumItem.tooltip}
                key={`${premiumItem.title}-${index}`}
              />
            ))}
          </React.Fragment>
        </ABTest>
        <ABTest variant="A" experiment="premiumTooltips_May2022">
          <React.Fragment>
            {premiumList.map((premiumItem, index) => (
              <ComparisonListItem title={premiumItem.title} key={`${premiumItem.title}-${index}`} />
            ))}
          </React.Fragment>
        </ABTest>
        <ABTest variant="B" experiment="premiumTooltips_May2022">
          <React.Fragment>
            {premiumList.map((premiumItem, index) => (
              <ComparisonListItem
                title={premiumItem.title}
                tooltip={premiumItem.tooltip}
                key={`${premiumItem.title}-${index}`}
              />
            ))}
          </React.Fragment>
        </ABTest>
      </div>
    </div>
  );
}
